<script>
  /**
   * Qualified signature page (firma qualificata) — admin only.
   *
   * Replaces the desktop-signing round trip: upload a PDF, place the visible
   * signature on the page, download the signed result.
   *
   * The confirmation step before signing is not ceremony. The certificate is an
   * automatic qualified one, so the click that starts a signature *is* the
   * signature — there is no PIN prompt, no OTP, and no undo, and the result
   * carries the same legal weight as a handwritten signature. The dialog is the
   * only moment a mistake can still be caught.
   */
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import SignaturePlacer from '../components/SignaturePlacer.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import { signaturesApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    PenTool, Upload, FileText, Download, Loader, AlertCircle, CheckCircle,
    ShieldAlert, Eye, EyeOff, FileSignature, History
  } from 'lucide-svelte';

  /** 'pades' places a visible signature on a PDF; 'cades' wraps any file in a .p7m. */
  let mode = 'pades';

  let status = null;
  let statusLoading = true;
  let message = null;

  // PAdES flow
  let job = null;
  let uploading = false;
  let signing = false;
  let signed = null;
  let currentPage = 1;
  let rect = null;
  let naturalSize = null;
  let imageVisible = true;
  let reason = '';
  let location = '';
  let signerName = '';
  let confirming = false;

  // CAdES flow
  let cadesFile = null;
  let cadesSigning = false;

  // Audit
  let auditEntries = [];
  let auditLoading = false;
  let showAudit = false;

  $: currentPageSize = job?.pages?.find((p) => p.page === currentPage) || null;
  $: pageImageUrl = job ? signaturesApi.pageImageUrl(job.id, currentPage) : null;

  onMount(async () => {
    try {
      status = await signaturesApi.status();
      signerName = status.defaultSignerName || '';
    } catch (err) {
      console.error('[Signatures] Status check failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      statusLoading = false;
    }
  });

  /**
   * Reset the placement state so a new document never inherits the previous
   * document's box — a box valid on A4 is off the page on A5.
   */
  function resetPlacement() {
    rect = null;
    naturalSize = null;
    currentPage = 1;
    signed = null;
  }

  /**
   * Upload a PDF and open a signing job.
   * @param {Event} event Change event from the file input
   */
  async function handleUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (status && file.size > status.maxUploadBytes) {
      message = {
        type: 'error',
        text: $_('signatures.fileTooLarge', { values: { mb: Math.round(status.maxUploadBytes / 1024 / 1024) } }),
      };
      event.target.value = '';
      return;
    }

    uploading = true;
    message = null;
    resetPlacement();
    try {
      job = await signaturesApi.createJob(file);
      console.log(`[Signatures] Job ${job.id} opened, ${job.pageCount} page(s)`);
    } catch (err) {
      console.error('[Signatures] Upload failed:', err);
      message = { type: 'error', text: err.message };
      job = null;
    } finally {
      uploading = false;
      event.target.value = '';
    }
  }

  /** Apply the qualified signature. Irreversible once it succeeds. */
  async function performSign() {
    confirming = false;
    signing = true;
    message = null;
    try {
      const payload = {
        page: currentPage,
        imageVisible,
        reason: reason.trim() || undefined,
        location: location.trim() || undefined,
        signerName: signerName.trim() || undefined,
      };
      if (imageVisible) {
        // Natural image pixels, together with the grid they were measured
        // against; the backend flips the y axis and converts to PDF points.
        payload.rect = {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          imageWidth: naturalSize.width,
          imageHeight: naturalSize.height,
        };
      }
      signed = await signaturesApi.sign(job.id, payload);
      message = { type: 'success', text: $_('signatures.signSuccess') };
      if (showAudit) loadAudit();
    } catch (err) {
      console.error('[Signatures] Signing failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      signing = false;
    }
  }

  /** Download the signed PDF. */
  async function downloadSigned() {
    try {
      const blob = await signaturesApi.download(job.id);
      triggerDownload(blob, signed.outputFilename);
    } catch (err) {
      console.error('[Signatures] Download failed:', err);
      message = { type: 'error', text: err.message };
    }
  }

  /** CAdES-sign the selected file and download the .p7m. */
  async function signCades() {
    if (!cadesFile) return;
    cadesSigning = true;
    message = null;
    try {
      const blob = await signaturesApi.signCades(cadesFile);
      triggerDownload(blob, `${cadesFile.name}.p7m`);
      message = { type: 'success', text: $_('signatures.signSuccess') };
      cadesFile = null;
      if (showAudit) loadAudit();
    } catch (err) {
      console.error('[Signatures] CAdES signing failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      cadesSigning = false;
    }
  }

  /**
   * Hand a blob to the browser as a download.
   * @param {Blob} blob Content
   * @param {string} filename Suggested filename
   */
  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /** Load the audit trail. */
  async function loadAudit() {
    auditLoading = true;
    try {
      const response = await signaturesApi.audit({ limit: 50 });
      auditEntries = response.entries || [];
    } catch (err) {
      console.error('[Signatures] Audit load failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      auditLoading = false;
    }
  }

  /** Toggle the audit panel, loading it on first open. */
  function toggleAudit() {
    showAudit = !showAudit;
    if (showAudit && auditEntries.length === 0) loadAudit();
  }

  /**
   * @param {string} value ISO instant
   * @returns {string} localised date and time
   */
  function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center space-x-4">
      <div class="p-3 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #00bfff, #ff00ff);">
        <PenTool size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('signatures.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatures.subtitle')}</p>
      </div>
    </div>

    {#if statusLoading}
      <div class="flex items-center space-x-2 text-gray-500">
        <Loader size={18} class="animate-spin" />
        <span>{$_('common.loading')}</span>
      </div>
    {:else if status && !status.enabled}
      <div class="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800">
        <div class="flex items-start space-x-2">
          <ShieldAlert size={20} class="shrink-0 mt-0.5" />
          <div>
            <p class="font-semibold">{$_('signatures.notConfigured')}</p>
            {#if status.missing?.length}
              <p class="text-sm mt-1 font-mono">{status.missing.join(', ')}</p>
            {/if}
          </div>
        </div>
      </div>
    {:else}
      {#if status?.trustAnchor?.misconfigured}
        <!-- A misconfigured anchor looks exactly like a customer's self-signed
             certificate on every document: red badge, "catena non verificata".
             This is the one place the person who can fix the path will look, so
             it says which variable and which path, and that it is the server's
             problem rather than the document's. -->
        <div class="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-2 border-red-400">
          <div class="flex items-start space-x-2">
            <ShieldAlert size={20} class="shrink-0 mt-0.5" />
            <div>
              <p class="font-bold">{$_('signatures.trustMisconfiguredTitle')}</p>
              <p class="text-sm mt-1">{$_('signatures.trustMisconfiguredBody')}</p>
              <ul class="text-sm mt-2 font-mono space-y-1">
                {#if status.trustAnchor.bundleReadable === false}
                  <li>SIGNATURE_TRUST_BUNDLE={status.trustAnchor.bundlePath}</li>
                {/if}
                {#if status.trustAnchor.nssDirReadable === false}
                  <li>SIGNATURE_NSS_DIR={status.trustAnchor.nssDir}</li>
                {/if}
              </ul>
            </div>
          </div>
        </div>
      {/if}

      {#if status?.sandbox}
        <!-- Loud on purpose: a sandbox signature looks real in a PDF viewer but
             carries a DEMO certificate and no legal value. -->
        <div class="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-2 border-purple-400">
          <div class="flex items-center space-x-2">
            <AlertCircle size={20} />
            <span class="font-bold">{$_('signatures.sandboxWarning')}</span>
          </div>
        </div>
      {/if}

      <!-- Legal notice -->
      <div class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200 border border-blue-200 dark:border-blue-800 text-sm">
        <div class="flex items-start space-x-2">
          <AlertCircle size={18} class="shrink-0 mt-0.5" />
          <p>{$_('signatures.legalNotice')}</p>
        </div>
      </div>

      {#if message}
        <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
          {#if message.type === 'success'}<CheckCircle size={20} />{:else}<AlertCircle size={20} />{/if}
          <span>{message.text}</span>
          <button class="ml-auto hover:opacity-70" onclick={() => (message = null)} aria-label={$_('common.close')}>&times;</button>
        </div>
      {/if}

      <!-- Mode switch -->
      <div class="flex space-x-2">
        <button
          class="btn {mode === 'pades' ? 'btn-primary' : 'btn-secondary'}"
          onclick={() => (mode = 'pades')}
        >
          <FileText size={16} class="mr-2" />{$_('signatures.modePades')}
        </button>
        <button
          class="btn {mode === 'cades' ? 'btn-primary' : 'btn-secondary'}"
          onclick={() => (mode = 'cades')}
        >
          <FileSignature size={16} class="mr-2" />{$_('signatures.modeCades')}
        </button>
      </div>

      {#if mode === 'pades'}
        <div class="card p-6 space-y-6">
          <!-- Upload -->
          <div>
            <label class="label" for="pdf-upload">{$_('signatures.uploadLabel')}</label>
            <input
              id="pdf-upload"
              type="file"
              accept="application/pdf,.pdf"
              class="input"
              disabled={uploading || signing}
              onchange={handleUpload}
            />
            {#if uploading}
              <p class="mt-2 text-sm text-gray-500 flex items-center space-x-2">
                <Loader size={14} class="animate-spin" />
                <span>{$_('signatures.uploading')}</span>
              </p>
            {/if}
          </div>

          {#if job}
            <div class="border-t-2 border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <p class="text-sm text-gray-600 dark:text-gray-300">
                <strong>{job.originalFilename}</strong>
                &middot; {$_('signatures.pageCount', { values: { count: job.pageCount } })}
              </p>

              <!-- Visible / invisible -->
              <div class="flex items-center space-x-3">
                <button
                  class="btn {imageVisible ? 'btn-primary' : 'btn-secondary'} text-xs"
                  onclick={() => (imageVisible = true)}
                  disabled={signing}
                >
                  <Eye size={14} class="mr-2" />{$_('signatures.visible')}
                </button>
                <button
                  class="btn {!imageVisible ? 'btn-primary' : 'btn-secondary'} text-xs"
                  onclick={() => (imageVisible = false)}
                  disabled={signing}
                >
                  <EyeOff size={14} class="mr-2" />{$_('signatures.invisible')}
                </button>
              </div>

              {#if imageVisible}
                <!-- Page picker -->
                {#if job.pageCount > 1}
                  <div>
                    <label class="label" for="page-select">{$_('signatures.page')}</label>
                    <select
                      id="page-select"
                      class="input max-w-xs"
                      bind:value={currentPage}
                      disabled={signing}
                      onchange={() => { rect = null; naturalSize = null; }}
                    >
                      {#each job.pages as page}
                        <option value={page.page}>
                          {page.page} / {job.pageCount}
                        </option>
                      {/each}
                    </select>
                  </div>
                {/if}

                <!-- Placement -->
                {#if currentPageSize && pageImageUrl}
                  <div>
                    <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">{$_('signatures.placeHint')}</p>
                    {#key `${job.id}-${currentPage}`}
                      <SignaturePlacer
                        imageUrl={pageImageUrl}
                        pageSize={currentPageSize}
                        bind:rect
                        bind:naturalSize
                        disabled={signing}
                      />
                    {/key}
                  </div>
                {/if}
              {:else}
                <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatures.invisibleHint')}</p>
              {/if}

              <!-- Metadata -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="label" for="signer-name">{$_('signatures.signerName')}</label>
                  <input id="signer-name" type="text" class="input" bind:value={signerName} disabled={signing} maxlength="120" />
                </div>
                <div>
                  <label class="label" for="reason">{$_('signatures.reason')}</label>
                  <input id="reason" type="text" class="input" bind:value={reason} disabled={signing} maxlength="200" />
                </div>
                <div>
                  <label class="label" for="location">{$_('signatures.location')}</label>
                  <input id="location" type="text" class="input" bind:value={location} disabled={signing} maxlength="200" />
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center space-x-3">
                {#if !signed}
                  <button
                    class="btn btn-primary"
                    disabled={signing || (imageVisible && (!rect || !naturalSize))}
                    onclick={() => (confirming = true)}
                  >
                    {#if signing}
                      <Loader size={16} class="mr-2 animate-spin" />{$_('signatures.signing')}
                    {:else}
                      <PenTool size={16} class="mr-2" />{$_('signatures.signAction')}
                    {/if}
                  </button>
                {:else}
                  <button class="btn btn-primary" onclick={downloadSigned}>
                    <Download size={16} class="mr-2" />{$_('signatures.download')}
                  </button>
                  <span class="text-xs font-mono text-gray-500 dark:text-gray-400 break-all">
                    SHA-256 {signed.outputSha256?.slice(0, 24)}…
                  </span>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      {:else}
        <!-- CAdES -->
        <div class="card p-6 space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-300">{$_('signatures.cadesHint')}</p>
          <div>
            <label class="label" for="cades-upload">{$_('signatures.uploadAnyLabel')}</label>
            <input
              id="cades-upload"
              type="file"
              class="input"
              disabled={cadesSigning}
              onchange={(e) => (cadesFile = e.target.files?.[0] || null)}
            />
          </div>
          <button class="btn btn-primary" disabled={!cadesFile || cadesSigning} onclick={signCades}>
            {#if cadesSigning}
              <Loader size={16} class="mr-2 animate-spin" />{$_('signatures.signing')}
            {:else}
              <FileSignature size={16} class="mr-2" />{$_('signatures.signAction')}
            {/if}
          </button>
        </div>
      {/if}

      <!-- Audit trail -->
      <div class="card p-6">
        <button class="flex items-center space-x-2 font-bold text-gray-900 dark:text-white" onclick={toggleAudit}>
          <History size={18} />
          <span>{$_('signatures.auditTitle')}</span>
        </button>

        {#if showAudit}
          {#if auditLoading}
            <p class="mt-4 text-sm text-gray-500 flex items-center space-x-2">
              <Loader size={14} class="animate-spin" /><span>{$_('common.loading')}</span>
            </p>
          {:else if auditEntries.length === 0}
            <p class="mt-4 text-sm text-gray-500">{$_('common.noData')}</p>
          {:else}
            <div class="mt-4 overflow-x-auto">
              <table class="table-modern">
                <thead>
                  <tr>
                    <th class="table-header">{$_('signatures.auditWhen')}</th>
                    <th class="table-header">{$_('signatures.auditFile')}</th>
                    <th class="table-header">{$_('signatures.auditType')}</th>
                    <th class="table-header">{$_('signatures.auditSource')}</th>
                    <th class="table-header">{$_('signatures.auditOutcome')}</th>
                    <th class="table-header">{$_('signatures.auditDigest')}</th>
                  </tr>
                </thead>
                <tbody>
                  {#each auditEntries as entry}
                    <tr class="table-row">
                      <td class="table-cell">{formatDateTime(entry.occurredAt)}</td>
                      <td class="table-cell">{entry.originalFilename}</td>
                      <td class="table-cell uppercase">{entry.signatureType}</td>
                      <td class="table-cell">
                        {entry.source === 'proposal' ? `#${entry.proposalId}` : $_('signatures.sourceStandalone')}
                      </td>
                      <td class="table-cell">
                        {#if entry.ok}
                          <span class="text-green-600 dark:text-green-400">✓</span>
                        {:else}
                          <span class="text-red-600 dark:text-red-400" title={entry.errorMessage}>
                            ✕ {entry.errorReason}
                          </span>
                        {/if}
                      </td>
                      <td class="table-cell font-mono text-xs">{entry.outputSha256?.slice(0, 16) || '—'}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
</Layout>

{#if confirming}
  <ConfirmModal
    title={$_('signatures.confirmTitle')}
    message={$_('signatures.confirmMessage', { values: { file: job?.originalFilename || '' } })}
    confirmText={$_('signatures.signAction')}
    confirmClass="btn-primary"
    loading={signing}
    on:confirm={performSign}
    on:cancel={() => (confirming = false)}
  />
{/if}
